import { Suspense, useCallback, useEffect, useState } from 'react';

import { ChevronDownIcon } from '@heroicons/react/16/solid';
import * as echarts from 'echarts';

import { Card } from '@/components/Card';
import { Loader } from '@/components/Loader';
import { AggregateSkuWapeChart } from '@/features/forecasts/components/forecast-details/AggregateSkuWapeChart';
import { FctComparisonChart } from '@/features/forecasts/components/forecast-details/FctComparisonChart';
import { FctForecastChart } from '@/features/forecasts/components/forecast-details/FctForecastChart';
import { ForecastEvaluationTable } from '@/features/forecasts/components/forecast-details/ForecastEvaluationTable';
import { WapeCallout } from '@/features/forecasts/components/forecast-details/WapeCallout';
import { OrderBehaviorDow } from '@/features/forecasts/components/order-behaviour/OrderBehaviorDow';
import { OrderBehaviorInterarrival } from '@/features/forecasts/components/order-behaviour/OrderBehaviorInterarrival';
import { OrderBehaviorLeadTime } from '@/features/forecasts/components/order-behaviour/OrderBehaviorLeadTime';
import { OrderBehaviorQty } from '@/features/forecasts/components/order-behaviour/OrderBehaviorQty';
import { BFG_MODEL_CONFIG, INCUMBENT_MODEL_CONFIG } from '@/features/forecasts/config/fctRuns';
import { FctForecastsProvider } from '@/features/forecasts/providers/FctForecastsProvider';
import { cls } from '@/utils/cls';

type DetailsChip = 'incumbent' | 'ours' | 'both';

const DETAILS_CHIPS: { id: DetailsChip; name: string }[] = [
  { id: 'incumbent', name: 'Incumbent' },
  { id: 'ours', name: 'BFG v2.2' },
  { id: 'both', name: 'Comparison' },
];

type Props = {
  actions?: React.ReactNode;
};

export function OrderForecastGraphs({ actions }: Props) {
  const tabs = [
    { id: 'behavior', name: 'Historical Order Behavior' },
    { id: 'details', name: 'Forecast Details' },
    { id: 'performance', name: 'Forecast Performance' },
  ];
  const fallback = <Loader containerClass="border-0 py-6" />;
  const tempCls = 'flex flex-col gap-6 w-full';
  const chartGroup = 'order-forecast-graphs';

  const [tab, setTab] = useState('behavior');
  const [detailsChip, setDetailsChip] = useState<DetailsChip>('incumbent');

  useEffect(() => {
    echarts.connect(chartGroup);
    return () => {
      echarts.disconnect(chartGroup);
    };
  }, []);

  const handleChipSelect = useCallback((chip: DetailsChip) => {
    setDetailsChip(chip);
  }, []);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setTab(tabId);
      if (tabId === 'details') handleChipSelect('incumbent');
    },
    [handleChipSelect],
  );

  return (
    <Card className="flex flex-col gap-6 !pt-3">
      <div className="grid grid-cols-1 sm:hidden">
        <select
          value={tab}
          aria-label={'Select a tab'}
          className="col-start-1 row-start-1 w-full appearance-none font-semibold rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-[#274483]"
          onChange={(event) => handleTabChange(event.target.value)}
        >
          {tabs.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          aria-hidden="true"
          className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500 dark:fill-gray-400"
        />
      </div>
      <div className="hidden sm:block">
        <div className="border-b border-gray-200 dark:border-white/10">
          <nav aria-label="Tabs" className="-mb-px flex items-center space-x-6">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTabChange(t.id)}
                aria-current={t.id === tab ? 'page' : undefined}
                className={cls(
                  t.id === tab
                    ? 'border-[#274483] text-[#274483]'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'cursor-pointer border-b-2 px-1 py-2.5 text-xs font-semibold whitespace-nowrap',
                )}
              >
                {t.name}
              </button>
            ))}
            {actions && <div className="ml-auto my-1.5">{actions}</div>}
          </nav>
        </div>
      </div>

      {tab === 'behavior' && (
        <>
          <div className={tempCls}>
            <Suspense fallback={fallback}>
              <OrderBehaviorInterarrival chartGroup={chartGroup} />
              <OrderBehaviorLeadTime chartGroup={chartGroup} />
              <OrderBehaviorQty chartGroup={chartGroup} />
              <OrderBehaviorDow chartGroup={chartGroup} />
            </Suspense>
          </div>
        </>
      )}

      {tab === 'details' && (
        <FctForecastsProvider>
          <div className={cls(tempCls, '-mt-3')}>
            <div className="flex flex-wrap gap-3">
              {DETAILS_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => handleChipSelect(chip.id)}
                  className={cls(
                    detailsChip === chip.id
                      ? 'bg-[#274483] text-white border-[#274483]'
                      : 'border-[#274483] bg-white text-[#274483] hover:bg-gray-50',
                    'cursor-pointer rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  )}
                >
                  {chip.name}
                </button>
              ))}
            </div>

            {detailsChip === 'incumbent' && (
              <Card className="flex flex-col gap-2">
                <Suspense fallback={fallback}>
                  <FctForecastChart
                    chartGroup={chartGroup}
                    config={INCUMBENT_MODEL_CONFIG}
                    model="incumbent"
                    title="Incumbent"
                    headerExtra={<WapeCallout model="incumbent" />}
                  />
                </Suspense>
              </Card>
            )}

            {detailsChip === 'ours' && (
              <Card className="flex flex-col gap-2">
                <Suspense fallback={fallback}>
                  <FctForecastChart
                    chartGroup={chartGroup}
                    config={BFG_MODEL_CONFIG}
                    model="bfg"
                    title="BFG v2.2"
                    headerExtra={<WapeCallout model="bfg" />}
                  />
                </Suspense>
              </Card>
            )}

            {detailsChip === 'both' && (
              <div className="flex flex-col gap-4">
                <Card className="flex flex-col gap-2">
                  <Suspense fallback={fallback}>
                    <FctComparisonChart chartGroup={chartGroup} title="Comparison" />
                  </Suspense>
                </Card>
                <ForecastEvaluationTable />
              </div>
            )}
          </div>
        </FctForecastsProvider>
      )}

      {tab === 'performance' && (
        <div className={tempCls}>
          <Suspense fallback={fallback}>
            <AggregateSkuWapeChart chartGroup={chartGroup} />
          </Suspense>
        </div>
      )}
    </Card>
  );
}
